import { match } from "ts-pattern";

import type { Assessment } from "../types/assessment.js";
import type { PersonInvolvementKind } from "../types/involvement.js";
import type { EndingRefusal } from "../types/involvementEnding.js";
import { isAssessmentStatusUnderWay } from "./isAssessmentStatusUnderWay.js";

type AssessmentProgress = Pick<
  Assessment,
  "definitive" | "status" | "deletion"
>;

const definitiveUnderWay = (
  assessments: readonly AssessmentProgress[],
): boolean =>
  assessments.some(
    (assessment) =>
      assessment.definitive &&
      assessment.deletion === null &&
      isAssessmentStatusUnderWay(assessment.status),
  );

const definitiveAwaitingApproval = (
  assessments: readonly AssessmentProgress[],
): boolean =>
  assessments.some(
    (assessment) =>
      assessment.definitive &&
      assessment.deletion === null &&
      assessment.status === "submitted",
  );

/**
 * Why an involvement of this kind may not be ended, given the person's
 * assessments, or `undefined` where it may.
 */
export const endingRefusalOf = (
  kind: PersonInvolvementKind,
  assessments: readonly AssessmentProgress[],
): EndingRefusal | undefined =>
  match(kind)
    .returnType<EndingRefusal | undefined>()
    .with("created_by", () => "kept_by_the_record")
    .with("responsible_user", () => "replaced_instead")
    .with("designated_assessor", () =>
      definitiveUnderWay(assessments) ? "assessment_open" : undefined,
    )
    .with("approver", () =>
      definitiveAwaitingApproval(assessments) ? "awaiting_approval" : undefined,
    )
    .with(
      "duty_of_care",
      "placement",
      "assessor",
      "viewer",
      "shared_assessment_reader",
      () => undefined,
    )
    .exhaustive();
